import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import GSTVerificationModal from '../../components/GSTVerificationModal';
import useDraftManager from '../../hooks/useDraftManager';
import StationSelect from '../../components/common/StationSelect';
import { 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaUser, 
  FaMapMarkerAlt, 
  FaIdCard,
  FaFileInvoiceDollar,
  FaUsers,
  FaFilter,
  FaDownload,
  FaEye,
  FaTimes,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

// States list for station creation
const INDIAN_STATES = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Bhutan',
  'Nepal',
  'Bangladesh'
];

// Custom styles for react-select with dark mode support
const customSelectStyles = (darkMode) => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    backgroundColor: darkMode ? '#4b5563' : 'white',
    border: `2px solid ${state.isFocused ? (darkMode ? '#10b981' : '#10b981') : (darkMode ? '#6b7280' : '#d1d5db')}`,
    borderRadius: '0.75rem',
    fontSize: '14px',
    boxShadow: state.isFocused ? `0 0 0 4px ${darkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)'}` : 'none',
    '&:hover': {
      borderColor: state.isFocused ? (darkMode ? '#10b981' : '#10b981') : (darkMode ? '#9ca3af' : '#9ca3af')
    },
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? (darkMode ? 'linear-gradient(to right, #10b981, #14b8a6)' : 'linear-gradient(to right, #10b981, #14b8a6)')
      : state.isFocused 
        ? (darkMode ? '#374151' : '#d1fae5') 
        : (darkMode ? '#1f2937' : 'white'),
    color: state.isSelected ? 'white' : (darkMode ? '#f3f4f6' : '#374151'),
    fontSize: '14px',
    padding: '10px 14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:active': {
      backgroundColor: darkMode ? '#059669' : '#059669'
    }
  }),
  singleValue: (provided) => ({
    ...provided,
    color: darkMode ? '#f3f4f6' : '#374151',
    fontSize: '14px',
    fontWeight: '500'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: darkMode ? '#9ca3af' : '#9ca3af',
    fontSize: '14px'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: darkMode ? '#1f2937' : 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
    zIndex: 9999,
    overflow: 'hidden'
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '6px',
    borderRadius: '0.75rem',
    backgroundColor: darkMode ? '#1f2937' : 'white'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? (darkMode ? '#10b981' : '#10b981') : (darkMode ? '#9ca3af' : '#9ca3af'),
    transition: 'all 0.3s ease',
    '&:hover': {
      color: darkMode ? '#10b981' : '#10b981'
    }
  }),
  input: (provided) => ({
    ...provided,
    color: darkMode ? '#f3f4f6' : '#374151'
  })
});

const pageSizeOptions = [
  { value: 5, label: '5 per page' },
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' }
];

const SupplierManagement = () => {
  const { user } = useContext(AuthContext);
  const nameInputRef = useRef(null);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [suppliers, setSuppliers] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    station: '',
    pinCode: '',
    gstin: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Toast notification
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // GST Verification Modal state
  const [showGSTVerification, setShowGSTVerification] = useState(false);
  const [isGstReadOnly, setIsGstReadOnly] = useState(false);
  const [showGstPanFields, setShowGstPanFields] = useState(false);

  // Station creation modal state
  const [showCreateStation, setShowCreateStation] = useState(false);
  const [stationForm, setStationForm] = useState({
    name: '',
    state: '',
    createdBy: user?.user?._id
  });
  const [isCreatingStation, setIsCreatingStation] = useState(false);

  // Initialize draft manager
  const draftManager = useDraftManager({
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    featureType: 'supplierManagement',
    formData: !isEditing ? form : null,
    setFormData: setForm,
    autoDraft: true,
    autoDraftInterval: 5000,
    onError: (error) => {
      console.error('Draft error:', error);
    },
    onDraftSaved: () => {
      setNotificationMessage('Draft saved automatically');
      setShowNotification(true);
    },
    onDraftLoaded: (loadedData) => {
      console.log('Draft loaded:', loadedData);
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: 'Your previous draft has been loaded',
        timer: 2000,
        showConfirmButton: false
      });
    }
  });

  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => setShowNotification(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [currentPage, pageSize, searchQuery]);

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      setStations(response.data);
    } catch (error) {
      console.error('Error fetching stations:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch stations.',
        confirmButtonColor: '#3085d6'
      });
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const companyId = user?.user?.company?._id || user?.user?.company;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/suppliers`,
        {
          params: {
            page: currentPage,
            limit: pageSize,
            search: searchQuery,
            userCompanyId: companyId
          }
        }
      );

      if (response.data.success) {
        const { suppliers, total, totalPages } = response.data.data;
        setSuppliers(suppliers);
        setTotalSuppliers(total);
        setTotalPages(totalPages);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch suppliers.',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle GST verification modal data
  const handleGSTVerificationData = (gstData) => {
    console.log('GST Verification Data received:', gstData);
    
    if (gstData.gstin) {
      setForm(prev => ({
        ...prev,
        gstin: gstData.gstin,
        name: gstData.lgnm || prev.name, // Business name
        address: gstData.pradr?.adr || prev.address, // Principal Address
        pinCode: gstData.pradr?.pncd || prev.pinCode, // PIN Code from principal address
      }));
      
      setIsGstReadOnly(true);
      setShowGstPanFields(true);
      
      Swal.fire({
        icon: 'success',
        title: 'GST Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="text-sm text-gray-600 mb-2">✅ GSTIN: ${gstData.gstin}</p>
            <p class="text-sm text-gray-600 mb-2">✅ Business Name: ${gstData.lgnm || 'N/A'}</p>
            <p class="text-sm text-gray-600">✅ Data has been auto-filled</p>
          </div>
        `,
        timer: 3000,
        showConfirmButton: false
      });
    }
  };

  // Open GST verification modal
  const openGSTVerification = () => {
    console.log('Opening GST verification modal');
    setShowGSTVerification(true);
  };

  // Handle clear GST
  const handleClearGST = () => {
    Swal.fire({
      title: 'Clear GST Data?',
      text: 'This will clear the verified GST information',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, clear it'
    }).then((result) => {
      if (result.isConfirmed) {
        setForm(prev => ({
          ...prev,
          gstin: ''
        }));
        setIsGstReadOnly(false);
        setShowGstPanFields(false);
      }
    });
  };

  // Handle GST change (manual input)
  const handleGSTChange = (e) => {
    const value = e.target.value.toUpperCase();
    setForm(prev => ({ ...prev, gstin: value }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Name Required',
        text: 'Please enter supplier name',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (!form.station) {
      Swal.fire({
        icon: 'error',
        title: 'Station Required',
        text: 'Please select a station',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (form.pinCode && !/^\d{6}$/.test(form.pinCode)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid PIN Code',
        text: 'Please enter a valid 6-digit PIN code',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstin)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid GSTIN',
        text: 'Please enter a valid GSTIN number',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: 'Please enter a valid email address',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    if (form.phone && !/^\d{10}$/.test(form.phone)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Phone',
        text: 'Please enter a valid 10-digit phone number',
        confirmButtonColor: '#3085d6'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const url = isEditing
        ? `${process.env.REACT_APP_API_BASE_URL}/suppliers/${editingId}`
        : `${process.env.REACT_APP_API_BASE_URL}/suppliers`;

      // Prepare form data with createdBy field for new suppliers
      const formData = {
        ...form,
        ...((!isEditing) && { createdBy: user?.user?._id })
      };

      const response = await axios({
        method: isEditing ? 'put' : 'post',
        url,
        data: formData
      });

      if (response.data.success) {
        // Delete draft after successful save
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('✅ Draft deleted after successful supplier save');
          } catch (draftError) {
            console.error('⚠️ Failed to delete draft:', draftError);
            // Don't block user flow if draft deletion fails
          }
        }
        
        setNotificationMessage(isEditing ? 'Supplier updated successfully!' : 'Supplier created successfully!');
        setShowNotification(true);
        resetForm();
        fetchSuppliers();
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save supplier',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (supplier) => {
    // Delete any existing draft when editing a supplier
    if (draftManager.currentDraftId) {
      try {
        await draftManager.deleteDraft(draftManager.currentDraftId);
        console.log('✅ Draft deleted when editing supplier');
      } catch (draftError) {
        console.error('⚠️ Failed to delete draft on edit:', draftError);
        // Don't block user flow if draft deletion fails
      }
    }
    
    setIsEditing(true);
    setEditingId(supplier._id);
    setForm({
      name: supplier.name || '',
      station: supplier.station || '',
      pinCode: supplier.pinCode || '',
      gstin: supplier.gstin || '',
      contactPerson: supplier.contactPerson || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || ''
    });
    
    // If supplier has GST, show the fields
    if (supplier.gstin) {
      setShowGstPanFields(true);
      setIsGstReadOnly(false); // Allow editing existing GST
    }
    
    setTimeout(() => nameInputRef.current?.focus(), 100);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Supplier?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/suppliers/${id}`);
        setNotificationMessage('Supplier deleted successfully!');
        setShowNotification(true);
        fetchSuppliers();
      } catch (error) {
        console.error('Error deleting supplier:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete supplier',
          confirmButtonColor: '#3085d6'
        });
      }
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      station: '',
      pinCode: '',
      gstin: '',
      contactPerson: '',
      phone: '',
      email: '',
      address: ''
    });
    setIsEditing(false);
    setEditingId(null);
    setIsGstReadOnly(false);
    setShowGstPanFields(false);
  };

  // Station creation functions
  const resetStationForm = () => {
    setStationForm({
      name: '',
      state: '',
      createdBy: user?.user?._id
    });
  };

  const handleStationFormChange = (e) => {
    const { name, value } = e.target;
    setStationForm(prev => ({
      ...prev,
      [name]: name === 'name' ? value.toUpperCase() : value
    }));
  };

  const validateStationForm = () => {
    const errors = [];
    if (!stationForm.name.trim()) {
      errors.push('Station name is required');
    }
    if (!stationForm.state) {
      errors.push('State is required');
    }
    
    // Check for duplicate station name
    const duplicateStation = stations.find(
      s => s.name.toLowerCase() === stationForm.name.toLowerCase()
    );
    if (duplicateStation) {
      errors.push('A station with this name already exists');
    }
    
    return errors;
  };

  const handleCreateStation = async () => {
    const errors = validateStationForm();
    if (errors.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: errors.join('<br>'),
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    setIsCreatingStation(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/stations`, {
        name: stationForm.name,
        state: stationForm.state,
        createdBy: stationForm.createdBy
      });

      const newStation = response.data.station;
      
      // Refresh stations list
      await fetchStations();
      
      // Auto-select the newly created station
      setForm({ ...form, station: newStation._id });
      
      // Close modal and reset form
      setShowCreateStation(false);
      resetStationForm();
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Station created successfully',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error creating station:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to create station',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setIsCreatingStation(false);
    }
  };

  const viewSupplierDetails = (supplier) => {
    setSelectedSupplier(supplier);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Build station options for select
  const stationOptions = stations.map(station => ({
    value: station._id,
    label: station.name
  }));

  return (
    <div className={`p-3 sm:p-4 md:p-6 shadow-xl rounded-xl md:rounded-2xl transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Toast Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`${darkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-800 border-gray-200'} border rounded-xl shadow-2xl p-4 flex items-center space-x-3 min-w-[300px]`}>
            <FaCheckCircle className="text-green-500 text-xl flex-shrink-0" />
            <p className="font-medium">{notificationMessage}</p>
          </div>
        </div>
      )}

      {/* Header - Matching Customer Management */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className={`flex items-center justify-between p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl transition-colors duration-300 ${
          darkMode 
            ? 'bg-gradient-to-r from-green-900 via-teal-900 to-blue-900' 
            : 'bg-gradient-to-r from-green-600 via-teal-600 to-blue-600'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-2 sm:p-2.5 md:p-3 rounded-lg">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                Supplier Management
              </h2>
              <p className="text-green-100 text-xs sm:text-sm mt-0.5 sm:mt-1">Manage your supplier relationships</p>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-white font-medium">{isEditing ? 'Edit Mode' : 'Create Mode'}</span>
          </div>
        </div>
      </div>

      {/* -------------------------
          ADD / EDIT SUPPLIER SECTION
         ------------------------- */}
      <div className={`mb-4 sm:mb-5 md:mb-6 p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl border transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 shadow-md'
      }`}>
        <h3 className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 flex items-center transition-colors duration-300 ${
          darkMode ? 'text-gray-100' : 'text-gray-800'
        }`}>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
        </h3>
        
        <form onSubmit={handleSubmit}>
          {/* Row 1: Name, Station, PIN Code */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Supplier Name */}
            <div className="flex flex-col">
              <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Supplier Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
                placeholder="Enter supplier name"
                required
              />
            </div>

            {/* Station */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <StationSelect
                    value={form.station}
                    onChange={(stationId) => setForm({ ...form, station: stationId })}
                    stations={stations}
                    onCreateNew={() => {
                      resetStationForm();
                      setShowCreateStation(true);
                    }}
                    customSelectStyles={customSelectStyles(darkMode)}
                    darkMode={darkMode}
                    required={true}
                    label="Station"
                  />
                </div>
              </div>
            </div>

            {/* PIN Code */}
            <div className="flex flex-col">
              <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                PIN Code
              </label>
              <input
                type="text"
                value={form.pinCode}
                onChange={(e) => setForm({ ...form, pinCode: e.target.value })}
                maxLength="6"
                className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
                placeholder="6-digit PIN code"
              />
            </div>
          </div>

          {/* Row 2: GST Verification (Inline), Contact Person, Phone */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* GST Verification */}
            <div className="flex flex-col">
              {!showGstPanFields ? (
                <>
                  <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    GST Verification
                  </label>
                  <button
                    type="button"
                    onClick={openGSTVerification}
                    className={`px-4 py-2.5 rounded-lg transition-all text-sm flex items-center justify-center space-x-2 ${
                      darkMode 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white' 
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                    }`}
                    title="Verify GST with Government Portal"
                  >
                    <span>🔍</span>
                    <span>Verify GST Number</span>
                  </button>
                  <p className={`text-xs mt-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Verify GSTIN and auto-fill supplier details
                  </p>
                </>
              ) : (
                <>
                  <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    GSTIN {isGstReadOnly && <span className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>(Verified ✅)</span>}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="gstin"
                      value={form.gstin}
                      onChange={handleGSTChange}
                      placeholder="15-digit GST number"
                      maxLength="15"
                      disabled={isGstReadOnly}
                      className={`border rounded-lg p-2.5 flex-1 transition-colors duration-300 font-mono text-sm ${
                        isGstReadOnly 
                          ? darkMode 
                            ? 'bg-gray-700 text-gray-400 cursor-not-allowed border-gray-600' 
                            : 'bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300'
                          : darkMode
                            ? 'bg-gray-600 text-white border-gray-500 focus:ring-2 focus:ring-green-500'
                            : 'bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                      }`}
                    />
                    {form.gstin && (
                      <button
                        type="button"
                        onClick={handleClearGST}
                        className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                          darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'
                        }`}
                        title="Clear GST"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Contact Person */}
            <div className="flex flex-col">
              <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Contact Person
              </label>
              <input
                type="text"
                value={form.contactPerson}
                onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
                placeholder="Contact person name"
              />
            </div>

            {/* Phone */}
            <div className="flex flex-col">
              <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Phone Number
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                maxLength="10"
                className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
                placeholder="10-digit phone number"
              />
            </div>
          </div>

          {/* Row 3: Email and Address */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Email */}
            <div className="flex flex-col">
              <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
                placeholder="email@example.com"
              />
            </div>

            {/* Address - Full Width */}
            <div className="md:col-span-2 flex flex-col">
              <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Full Address
              </label>
              <textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows="2"
                className={`border rounded-lg p-2.5 w-full transition-colors duration-300 resize-none ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
                placeholder="Full address"
              />
            </div>
          </div>

            {/* Form Actions */}
            <div className={`flex items-center justify-end space-x-4 mt-6 pt-4 border-t transition-colors duration-300 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md ${
                    darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className={`px-8 py-3 rounded-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                  submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : darkMode
                    ? 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white'
                    : 'bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white'
                }`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {isEditing ? 'Update Supplier' : 'Create Supplier'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

      {/* -------------------------
          SEARCH & FILTERS SECTION  
         ------------------------- */}
      <div className={`mb-4 sm:mb-5 md:mb-6 p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl border transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 shadow-md'
      }`}>
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <label className={`block font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Search Suppliers
            </label>
            <div className="relative">
              <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, GSTIN, station, phone..."
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
                }`}
              />
            </div>
          </div>

          {/* Page Size Selector */}
          <div className="w-full sm:w-64">
            <label className={`block font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Items per Page
            </label>
            <Select
              value={pageSizeOptions.find(opt => opt.value === pageSize)}
              onChange={(selectedOption) => {
                setPageSize(selectedOption.value);
                setCurrentPage(1);
              }}
              options={pageSizeOptions}
              styles={customSelectStyles(darkMode)}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>
      </div>

      {/* -------------------------
          SUPPLIERS LIST TABLE
         ------------------------- */}
      <div className={`rounded-lg md:rounded-xl border overflow-hidden transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-white border-gray-200 shadow-md'
      }`}>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Loading suppliers...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block lg:hidden p-4 space-y-4">
                {suppliers.map((supplier) => (
                  <div 
                    key={supplier._id}
                    className={`rounded-2xl p-4 shadow-md border-l-4 border-blue-500 transition-all duration-300 ${
                      darkMode 
                        ? 'bg-gray-700 hover:bg-gray-600' 
                        : 'bg-white hover:bg-blue-50'
                    }`}
                  >
                    {/* Supplier Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <div className={`font-bold text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          <FaUser className="text-blue-500 flex-shrink-0" />
                          <span className="truncate">{supplier.name}</span>
                        </div>
                        <span className={`inline-block mt-1 px-3 py-1 rounded-lg text-xs font-bold ${
                          darkMode 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {supplier.id}
                        </span>
                      </div>
                    </div>

                    {/* Supplier Details Grid */}
                    <div className="space-y-2 text-sm">
                      {supplier.contactPerson && (
                        <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <FaUser className="text-purple-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium">Contact Person:</span>
                            <div>{supplier.contactPerson}</div>
                          </div>
                        </div>
                      )}

                      <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <FaMapMarkerAlt className="text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Station:</span>
                          <div>{stations.find(s => s._id === supplier.station)?.name || supplier.station}</div>
                        </div>
                      </div>

                      {supplier.phone && (
                        <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <FaPhone className="text-purple-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium">Phone:</span>
                            <div>{supplier.phone}</div>
                          </div>
                        </div>
                      )}

                      {supplier.email && (
                        <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <FaEnvelope className="text-orange-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium">Email:</span>
                            <div className="break-all">{supplier.email}</div>
                          </div>
                        </div>
                      )}

                      {supplier.gstin && (
                        <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <FaFileInvoiceDollar className="text-orange-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium">GSTIN:</span>
                            <div className="font-mono font-bold">{supplier.gstin}</div>
                          </div>
                        </div>
                      )}

                      {supplier.address && (
                        <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <FaMapMarkerAlt className="text-red-500 mt-1 flex-shrink-0" />
                          <div>
                            <span className="font-medium">Address:</span>
                            <div>{supplier.address}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className={`flex gap-2 mt-4 pt-4 border-t transition-colors duration-300 ${
                      darkMode ? 'border-gray-600' : 'border-gray-200'
                    }`}>
                      <button
                        onClick={() => viewSupplierDetails(supplier)}
                        className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                          darkMode
                            ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                        }`}
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => handleEdit(supplier)}
                        className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                          darkMode
                            ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30 border border-green-500/30'
                            : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                        }`}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(supplier._id)}
                        className={`py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                          darkMode
                            ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-500/30'
                            : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                        }`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}

                {suppliers.length === 0 && !loading && (
                  <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FaUsers className={`mx-auto text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className="text-lg font-medium">No suppliers found</p>
                    <p className="text-sm mt-2">Try adjusting your search or add a new supplier</p>
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className={`transition-colors duration-300 ${
                    darkMode 
                      ? 'bg-gradient-to-r from-green-900 via-teal-900 to-blue-900' 
                      : 'bg-gradient-to-r from-green-600 via-teal-600 to-blue-600'
                  }`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Supplier Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Station
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        GSTIN
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y transition-colors duration-300 ${
                    darkMode ? 'divide-gray-600' : 'divide-gray-200'
                  }`}>
                    {suppliers.map((supplier, index) => (
                      <tr 
                        key={supplier._id} 
                        className={`transition-colors duration-200 ${
                          darkMode 
                            ? `hover:bg-gray-600 ${index % 2 === 0 ? 'bg-gray-700' : 'bg-gray-700/50'}`
                            : `hover:bg-green-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`
                        }`}
                      >
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
                        darkMode ? 'text-green-400' : 'text-green-600'
                      }`}>
                        {supplier.id}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        <div className="font-medium">{supplier.name}</div>
                        {supplier.contactPerson && (
                          <div className={`text-xs mt-0.5 transition-colors duration-300 ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {supplier.contactPerson}
                          </div>
                        )}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {stations.find(s => s._id === supplier.station)?.name || supplier.station}
                      </td>
                      <td className={`px-6 py-4 text-sm transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {supplier.phone && (
                          <div>{supplier.phone}</div>
                        )}
                        {supplier.email && (
                          <div className={`text-xs mt-0.5 transition-colors duration-300 ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {supplier.email}
                          </div>
                        )}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {supplier.gstin ? (
                          <span className="font-mono font-medium">{supplier.gstin}</span>
                        ) : (
                          <span className={`italic transition-colors duration-300 ${
                            darkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            Not provided
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(supplier)}
                            className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-3 py-2 rounded-lg hover:from-green-600 hover:to-teal-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                          >
                            <FaEdit className="text-xs" />
                            <span className="font-medium">Edit</span>
                          </button>
                          <button
                            onClick={() => viewSupplierDetails(supplier)}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                          >
                            <FaEye className="text-xs" />
                            <span className="font-medium">View</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  
                  {suppliers.length === 0 && !loading && (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          <FaUsers className={`mx-auto text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                          <p className="text-lg font-medium">No suppliers found</p>
                          <p className="text-sm mt-2">Try adjusting your search or add a new supplier</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          )}

        {/* -------------------------
            PAGINATION
           ------------------------- */}
        {suppliers.length > 0 && (
          <div className={`px-4 sm:px-6 py-4 border-t transition-colors duration-300 ${
            darkMode 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className={`text-sm font-medium transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Showing <strong className="text-green-600">{((currentPage - 1) * pageSize) + 1}</strong> to{' '}
                <strong className="text-green-600">{Math.min(currentPage * pageSize, totalSuppliers)}</strong> of{' '}
                <strong className="text-green-600">{totalSuppliers}</strong> suppliers
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    currentPage === 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600 shadow-sm'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-green-50 hover:border-green-400 shadow-sm'
                  }`}
                  aria-label="Previous page"
                >
                  <FaChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = i + Math.max(1, currentPage - 2);
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg scale-105'
                            : darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 shadow-sm'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-green-50 hover:border-green-400 shadow-sm'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    currentPage === totalPages
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600 shadow-sm'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-green-50 hover:border-green-400 shadow-sm'
                  }`}
                  aria-label="Next page"
                >
                  <FaChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Supplier Modal */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`${darkMode ? 'bg-gradient-to-r from-green-900 via-teal-900 to-blue-900' : 'bg-gradient-to-r from-green-600 via-teal-600 to-blue-600'} px-6 py-4 flex items-center justify-between rounded-t-2xl`}>
              <h3 className="text-2xl font-bold text-white flex items-center">
                <FaEye className="mr-3" />
                Supplier Details
              </h3>
              <button
                onClick={() => setSelectedSupplier(null)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all duration-300"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Supplier ID
                  </label>
                  <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedSupplier.id}
                  </p>
                </div>
                
                <div>
                  <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Supplier Name
                  </label>
                  <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedSupplier.name}
                  </p>
                </div>
                
                {selectedSupplier.contactPerson && (
                  <div>
                    <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Contact Person
                    </label>
                    <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSupplier.contactPerson}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Station
                  </label>
                  <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stations.find(s => s._id === selectedSupplier.station)?.name || selectedSupplier.station}
                  </p>
                </div>
                
                {selectedSupplier.pinCode && (
                  <div>
                    <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      PIN Code
                    </label>
                    <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSupplier.pinCode}
                    </p>
                  </div>
                )}
                
                {selectedSupplier.gstin && (
                  <div>
                    <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      GSTIN
                    </label>
                    <p className={`mt-1 text-lg font-mono ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSupplier.gstin}
                    </p>
                  </div>
                )}
                
                {selectedSupplier.phone && (
                  <div>
                    <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Phone Number
                    </label>
                    <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSupplier.phone}
                    </p>
                  </div>
                )}
                
                {selectedSupplier.email && (
                  <div>
                    <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Email Address
                    </label>
                    <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSupplier.email}
                    </p>
                  </div>
                )}
                
                {selectedSupplier.address && (
                  <div className="md:col-span-2">
                    <label className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Address
                    </label>
                    <p className={`mt-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSupplier.address}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Station Creation Modal */}
      {showCreateStation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className={`rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 animate-slide-up ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-6 border-b transition-colors duration-300 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-br from-green-500 to-teal-500 p-3 rounded-xl shadow-lg">
                  <FaMapMarkerAlt className="text-white text-xl" />
                </div>
                <div>
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Create New Station
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Add a new station location
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCreateStation(false);
                  resetStationForm();
                }}
                className={`p-2 rounded-lg transition-all duration-300 hover:rotate-90 ${
                  darkMode 
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                disabled={isCreatingStation}
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Station Name */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <FaBuilding className="inline mr-1.5 text-xs" />
                  Station Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={stationForm.name}
                  onChange={handleStationFormChange}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:scale-[1.01] ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-500 focus:ring-4 focus:ring-green-500 focus:ring-opacity-20' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-4 focus:ring-green-500 focus:ring-opacity-20'
                  }`}
                  placeholder="e.g., MUMBAI, DELHI, BANGALORE"
                  autoFocus
                  disabled={isCreatingStation}
                />
                <p className={`mt-1.5 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Station name will be converted to uppercase
                </p>
              </div>

              {/* State */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <FaMapMarkerAlt className="inline mr-1.5 text-xs" />
                  State <span className="text-red-500">*</span>
                </label>
                <Select
                  value={stationForm.state ? { value: stationForm.state, label: stationForm.state } : null}
                  onChange={(selectedOption) => setStationForm({ ...stationForm, state: selectedOption?.value || '' })}
                  options={INDIAN_STATES.map(state => ({ value: state, label: state }))}
                  styles={customSelectStyles(darkMode)}
                  placeholder="Select state or UT..."
                  isClearable
                  isSearchable
                  isDisabled={isCreatingStation}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`flex items-center justify-end space-x-3 p-6 border-t transition-colors duration-300 ${
              darkMode ? 'border-gray-700 bg-gray-900 bg-opacity-50' : 'border-gray-200 bg-gray-50'
            } rounded-b-2xl`}>
              <button
                type="button"
                onClick={() => {
                  setShowCreateStation(false);
                  resetStationForm();
                }}
                disabled={isCreatingStation}
                className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                  darkMode 
                    ? 'bg-gray-700 text-white hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-md`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateStation}
                disabled={isCreatingStation}
                className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                  darkMode 
                    ? 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700' 
                    : 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600'
                } text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl`}
              >
                {isCreatingStation ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Creating...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <FaPlus className="text-sm" />
                    <span>Create Station</span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GST Verification Modal */}
      <GSTVerificationModal
        isOpen={showGSTVerification}
        onClose={() => setShowGSTVerification(false)}
        onVerificationSuccess={handleGSTVerificationData}
        darkMode={darkMode}
      />
    </div>
  );
};

export default SupplierManagement;
